<template>
  <v-flex
    id="calculation"
    class="question"
  >
    <h2 class="display-3 display-3_dark mb-4">
      {{ $t('sheet.sheetOne.reflection.calculation.title') }}
    </h2>
    <v-layout wrap>
      <v-flex
        xs12
        class="mb-3"
      >
        <v-layout
          align-center
          :wrap="$vuetify.breakpoint.xs"
        >
          <v-flex
            lg2
            sm4
            xs12
            class="text-dark f-2 fw-600"
          >
            <span
              class="f-28"
              :class="{ 'sz_red--text': familyReceipt < 0 }"
            >{{
              familyReceipt
            }}</span>
            zł
          </v-flex>
          <v-flex
            lg10
            sm8
            xs12
          >
            <p class="text-dark f-2 fw-600 mb-0">
              {{ $t('sheet.sheetOne.reflection.calculation.family_income') }}
            </p>
            <p class="mb-0">
              {{ $t('sheet.sheetOne.reflection.calculation.family_income_hint') | brackets }}
            </p>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex
        xs12
        class="mb-4"
      >
        <v-layout
          align-center
          :wrap="$vuetify.breakpoint.xs"
        >
          <v-flex
            lg2
            sm4
            xs12
            class="text-dark f-2 fw-600"
          >
            <span
              class="f-28"
              :class="{ 'sz_red--text': familyReceiptPerMember < 0 }"
            >{{
              familyReceiptPerMember
            }}</span>
            zł
          </v-flex>
          <v-flex
            lg10
            sm8
            xs12
          >
            <p class="text-dark f-2 fw-600 mb-0">
              {{ $t('sheet.sheetOne.reflection.calculation.income_per_member') }}
            </p>
            <p class="mb-0">
              {{ $t('sheet.sheetOne.reflection.calculation.income_per_member_hint') | brackets }}
            </p>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex xs12>
        <InfoAlert
          v-if="familyReceipt < 0 || familyReceiptPerMember < 0"
          class="mb-4"
          :warn="false"
          :text="$t('sheet.sheetOne.reflection.calculation.income_warning')"
        />
      </v-flex>
      <v-flex xs12>
        <v-divider />
      </v-flex>
    </v-layout>
    <h4 class="display-1 display-1_dark mt-3">
      {{ $t('sheet.sheetOne.reflection.calculation.detailed_calculation') | addColons }}
    </h4>
    <v-layout
      class="text-dark mt-2"
      :wrap="$vuetify.breakpoint.smAndDown"
    >
      <v-flex
        md6
        sm12
        class="px-3"
      >
        <v-layout
          column
          justify-space-between
          fill-height
        >
          <v-flex>
            <p class="fw-600 f-1 mb-2">
              {{ $t('sheet.sheetOne.reflection.calculation.summary_receipts') }}
            </p>
            <v-layout
              justify-space-between
              class="f-1 mb-1"
            >
              <v-flex>{{ $t('sheet.sheetOne.reflection.calculation.work') }}</v-flex>
              <v-flex class="none-growth">
                {{ meetingData.cashReceipts.totalReceiptsAmount | currency }}
              </v-flex>
            </v-layout>
            <v-layout
              justify-space-between
              class="f-1"
            >
              <v-flex>{{ $t('sheet.sheetOne.reflection.calculation.another_sources') }}</v-flex>
              <v-flex class="none-growth">
                {{ meetingData.cashReceipts.totalAnotherReceiptsAmount | currency }}
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex class="none-growth">
            <moneySummary
              :title="$t('sheet.sheetOne.reflection.calculation.amount')"
              :value="receiptsSummary"
            />
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex
        md6
        sm12
        class="px-3"
        :class="[{ 'mt-3': $vuetify.breakpoint.smAndDown }]"
      >
        <v-layout
          column
          justify-space-between
          fill-height
        >
          <v-flex>
            <p class="fw-600 f-1 mb-2">
              {{ $t('sheet.sheetOne.reflection.calculation.summary_costs') }}
            </p>
            <v-layout
              justify-space-between
              class="f-1 mb-1"
            >
              <v-flex>{{ $t('sheet.sheetOne.reflection.calculation.house') }}</v-flex>
              <v-flex class="none-growth">
                {{ maintenanceExpensesAmount | currency }}
              </v-flex>
            </v-layout>
            <v-layout
              v-if="meetingData.maintenanceExpenses.nurseryCost"
              justify-space-between
              class="f-1"
            >
              <v-flex>
                {{ $t('sheet.sheetOne.reflection.calculation.kids') }}
                <span
                  v-if="!takeJob"
                  class="f-05"
                >{{
                  $t('sheet.sheetOne.reflection.calculation.not_deducted') | brackets
                }}</span>
              </v-flex>
              <v-flex class="none-growth">
                {{ meetingData.maintenanceExpenses.nurseryCost | currency }}
              </v-flex>
            </v-layout>
            <v-layout
              v-if="meetingData.expensesForMedicine.totalMedicineAmount"
              justify-space-between
              class="f-1"
            >
              <v-flex>{{ $t('sheet.sheetOne.reflection.calculation.medicine') }}</v-flex>
              <v-flex class="none-growth">
                {{ meetingData.expensesForMedicine.totalMedicineAmount | currency }}
              </v-flex>
            </v-layout>
            <v-layout
              v-if="meetingData.credits.totalAmount"
              justify-space-between
              class="f-1"
            >
              <v-flex>
                {{ $t('sheet.sheetOne.reflection.calculation.credits') }}
                <span
                  v-if="
                    meetingData.credits.creditReason === credits.OTHER_CREDIT_REASON ||
                      meetingData.credits.regularCreditRepair !== 'yes'
                  "
                  class="f-05"
                >
                  {{ $t('sheet.sheetOne.reflection.calculation.not_deducted') | brackets }}</span>
              </v-flex>
              <v-flex class="none-growth">
                {{ meetingData.credits.totalAmount | currency }}
              </v-flex>
            </v-layout>
            <v-layout
              v-if="
                meetingData.credits.informalLiabilityTotalAmount &&
                  meetingData.credits.informalLiabilityReason !==
                  credits.OTHER_INFORMAL_LIABILITY_REASON
              "
              justify-space-between
              class="f-1"
            >
              <v-flex>
                {{ $t('sheet.sheetOne.reflection.calculation.liability') }}
                <span
                  v-if="
                    meetingData.credits.informalLiabilityReason ===
                      credits.OTHER_INFORMAL_LIABILITY_REASON ||
                      meetingData.credits.informalLiabilityPending !== 'yes'
                  "
                  class="f-05"
                >
                  {{ $t('sheet.sheetOne.reflection.calculation.not_deducted') | brackets }}</span>
              </v-flex>
              <v-flex class="none-growth">
                {{ meetingData.credits.informalLiabilityTotalAmount | currency }}
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex class="none-growth">
            <moneySummary
              :title="$t('sheet.sheetOne.reflection.calculation.amount')"
              :value="maintenanceAmount"
            />
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-divider class="my-4" />
    <p class="mb-0 required-block_left">
      {{ $t('sheet.fields_required') }}
    </p>
  </v-flex>
</template>

<script>
import filters from '@/constants/filters';
import credits from '@/constants/sheet/credits';
import incomesCalculation from '@/mixins/incomesCalculation';

import moneySummary from '@/components/widgets/sheet/moneySummary';
import InfoAlert from '@/components/widgets/InfoAlert';

export default {
    name: 'IncomeCalculation',
    filters: {
        ...filters
    },
    components: {
        InfoAlert,
        moneySummary
    },
    mixins: [incomesCalculation],
    data: () => credits,
    computed: {
        meetingData () {
            return this.$store.getters['sheet/getMeetingFields'];
        }
    }
};
</script>

<style scoped></style>
